/**
 * persistRuntimeSnapshot.js
 *
 * Serializes the compact operational runtime state to sessionStorage.
 *
 * What is persisted (compact, pastoral-safe):
 *  - currentModule, currentRoute, activeEntity, lastAction
 *  - recentModules (max 5 — module trail, not full events)
 *  - operationalMode (derived, not raw)
 *  - recentSignals (max 5, id+score+confidence only — no triggers or raw data)
 *  - timestamp
 *
 * What is NOT persisted:
 *  - Full timeline events
 *  - Message history or LLM responses
 *  - Pastoral content, notes, or entity details
 *  - Any user-authored text
 */

import { getState }                        from "../kevia-runtime/runtimeStore";
import { getRecentModules, getOperationalMode } from "../kevia-runtime/runtimeSelectors";
import { getActiveSignals }                from "../kevia-signals";
import { SCHEMA_VERSION, SNAPSHOT_KEY }    from "./persistenceSchema";
import { isSafeSize }                      from "./persistenceGuards";
import { compactWorkflowSummary }          from "../kevia-workflows/workflowMemory";

export function persistRuntimeSnapshot() {
  try {
    const state    = getState();
    const snapshot = {
      _schema:        SCHEMA_VERSION,
      _timestamp:     Date.now(),
      currentModule:  state.currentModule  ?? null,
      currentRoute:   state.currentRoute   ?? null,
      activeEntity:   state.activeEntity   ?? null,
      lastAction:     state.lastAction     ?? null,
      recentModules:  getRecentModules(5),
      operationalMode: getOperationalMode(),
      recentSignals:  getActiveSignals()
        .slice(0, 5)
        .map(({ id, score, confidence }) => ({ id, score, confidence })),
      workflowSummary: compactWorkflowSummary(),
    };

    const json = JSON.stringify(snapshot);
    if (!isSafeSize(json)) return false;

    sessionStorage.setItem(SNAPSHOT_KEY, json);
    return true;
  } catch {
    return false;
  }
}
